/**
 * Filter bar for the paid media dashboard pages.
 * Holds pending filter state locally — queries fire when "Apply" is clicked or
 * Enter is pressed in the title search.
 * Date presets are a UI convenience — they set start/end dates which are the
 * actual source of truth (stored in URL params by the parent).
 */
import React, { useState, useEffect } from 'react';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Input from '@amzn/meridian/input';
import Select, { SelectOption } from '@amzn/meridian/select';
import DatePicker from '@amzn/meridian/date-picker';
import { DATE_PRESETS } from 'src/utils/datePresets';

export interface DashboardFilters {
  startDate: string;
  endDate: string;
  title: string;
  country: string[];
  campaignId: string[];
  objectiveType: string[];
  advertiserId: string[];
  age: string[];
  gender: string[];
}

export type FilterKey = 'campaignId' | 'objectiveType' | 'country' | 'advertiserId' | 'age' | 'gender';

interface DashboardFilterBarProps {
  filters: DashboardFilters;
  filterOptions: Record<string, string[]>;
  onApply: (filters: DashboardFilters) => void;
  filterLabelOverrides?: Partial<Record<FilterKey, string>>;
  showTitleSearch?: boolean;
}

/**
 * Determines which preset matches the given start/end dates. Returns 'custom'
 * if no preset matches — this fixes the bug where loading from a bookmarked
 * URL would always show "Last 7 Days" regardless of the actual date range.
 */
const derivePreset = (startDate: string, endDate: string): string => {
  for (const p of DATE_PRESETS) {
    if (p.value === 'custom') continue;
    const range = p.getRange();
    if (range.startDate === startDate && range.endDate === endDate) return p.value;
  }
  return 'custom';
};

export const DashboardFilterBar: React.FC<DashboardFilterBarProps> = ({
  filters,
  filterOptions,
  onApply,
  filterLabelOverrides,
  showTitleSearch,
}) => {
  const [pending, setPending] = useState<DashboardFilters>(filters);
  const [preset, setPreset] = useState<string>(() => derivePreset(filters.startDate, filters.endDate));
  const [campaignSearch, setCampaignSearch] = useState('');
  const [objectiveSearch, setObjectiveSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [advertiserSearch, setAdvertiserSearch] = useState('');
  const [ageSearch, setAgeSearch] = useState('');
  const [genderSearch, setGenderSearch] = useState('');
  const today = new Intl.DateTimeFormat('en-CA').format(new Date());

  useEffect(() => {
    setPending(filters);
    setPreset(derivePreset(filters.startDate, filters.endDate));
  }, [filters]);

  const update = (field: keyof DashboardFilters, value: DashboardFilters[keyof DashboardFilters]) => {
    setPending((prev) => ({ ...prev, [field]: value }));
  };

  const handlePresetChange = (value: string) => {
    setPreset(value);
    if (value === 'custom') return;
    const presetConfig = DATE_PRESETS.find(p => p.value === value);
    if (presetConfig) {
      const { startDate, endDate } = presetConfig.getRange();
      setPending((prev) => ({ ...prev, startDate, endDate }));
    }
  };

  return (
    <Row alignmentVertical="bottom" spacing="300" wrap="down">
      <Select data-testid="dashboard-date-preset" label="Date Range" size="small" value={preset} width={160} onChange={handlePresetChange}>
        {DATE_PRESETS.map((p) => (
          <SelectOption key={p.value} label={p.label} value={p.value} />
        ))}
      </Select>
      {preset === 'custom' && (
        <>
          <DatePicker data-testid="dashboard-start-date" disabledDates={(d: string) => d > today} label="Start Date" size="small" value={pending.startDate} width={180} onChange={(val: string) => { update('startDate', val); }} />
          <DatePicker data-testid="dashboard-end-date" disabledDates={(d: string) => d > today} label="End Date" size="small" value={pending.endDate} width={180} onChange={(val: string) => { update('endDate', val); }} />
        </>
      )}
      {showTitleSearch && (
        <Input data-testid="title-search" label="Title" size="small" value={pending.title} width={250} onChange={(val: string) => { update('title', val); }} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) onApply(pending); }} />
      )}
      {filterOptions.campaignId && (
        <Select data-testid="campaign-select" label={filterLabelOverrides?.campaignId ?? 'Campaign'} placeholder="All" searchQuery={campaignSearch} size="small" value={pending.campaignId} width={300} onChange={(val: string[]) => { update('campaignId', val); }} onSearch={setCampaignSearch}>
          {filterOptions.campaignId.filter((c) => c.toLowerCase().includes(campaignSearch.toLowerCase())).map((c) => (
            <SelectOption key={c} label={c} value={c} />
          ))}
        </Select>
      )}
      {filterOptions.objectiveType && (
        <Select data-testid="objective-select" label={filterLabelOverrides?.objectiveType ?? 'Objective'} placeholder="All" searchQuery={objectiveSearch} size="small" value={pending.objectiveType} width={200} onChange={(val: string[]) => { update('objectiveType', val); }} onSearch={setObjectiveSearch}>
          {filterOptions.objectiveType.filter((o) => o.toLowerCase().includes(objectiveSearch.toLowerCase())).map((o) => (
            <SelectOption key={o} label={o} value={o} />
          ))}
        </Select>
      )}
      {filterOptions.country && (
        <Select data-testid="country-select" label={filterLabelOverrides?.country ?? 'Country'} placeholder="All" searchQuery={countrySearch} size="small" value={pending.country} width={150} onChange={(val: string[]) => { update('country', val); }} onSearch={setCountrySearch}>
          {filterOptions.country.filter((c) => c.toLowerCase().includes(countrySearch.toLowerCase())).map((c) => (
            <SelectOption key={c} label={c} value={c} />
          ))}
        </Select>
      )}
      {filterOptions.advertiserId && (
        <Select data-testid="advertiser-select" label={filterLabelOverrides?.advertiserId ?? 'Advertiser'} placeholder="All" searchQuery={advertiserSearch} size="small" value={pending.advertiserId} width={250} onChange={(val: string[]) => { update('advertiserId', val); }} onSearch={setAdvertiserSearch}>
          {filterOptions.advertiserId.filter((a) => a.toLowerCase().includes(advertiserSearch.toLowerCase())).map((a) => (
            <SelectOption key={a} label={a} value={a} />
          ))}
        </Select>
      )}
      {filterOptions.age && (
        <Select data-testid="age-select" label={filterLabelOverrides?.age ?? 'Age Range'} placeholder="All" searchQuery={ageSearch} size="small" value={pending.age} width={150} onChange={(val: string[]) => { update('age', val); }} onSearch={setAgeSearch}>
          {filterOptions.age.filter((a) => a.toLowerCase().includes(ageSearch.toLowerCase())).map((a) => (
            <SelectOption key={a} label={a} value={a} />
          ))}
        </Select>
      )}
      {filterOptions.gender && (
        <Select data-testid="gender-select" label={filterLabelOverrides?.gender ?? 'Audience Gender'} placeholder="All" searchQuery={genderSearch} size="small" value={pending.gender} width={150} onChange={(val: string[]) => { update('gender', val); }} onSearch={setGenderSearch}>
          {filterOptions.gender.filter((g) => g.toLowerCase().includes(genderSearch.toLowerCase())).map((g) => (
            <SelectOption key={g} label={g} value={g} />
          ))}
        </Select>
      )}
      <Button data-testid="dashboard-apply-filters" size="small" type="primary" onClick={() => { onApply(pending); }}>
        Apply
      </Button>
      <Button data-testid="dashboard-clear-filters" size="small" type="tertiary" onClick={() => {
        const cleared: DashboardFilters = { ...filters, title: '', country: [], campaignId: [], objectiveType: [], advertiserId: [], age: [], gender: [] };
        setPending(cleared);
        onApply(cleared);
      }}>
        Clear
      </Button>
    </Row>
  );
};
